import React from 'react';
import { Card } from '@/components/ui/card';
import { Network, Users, GitBranch, Calendar } from 'lucide-react';
import { MultiAgent } from '@/api/multi-agent/multi_agent.types';
import { motion } from 'framer-motion';
import { FadeId } from '@/components/ui/fade-id';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface MultiAgentCardProps {
    agent: MultiAgent;
    onClick: (agent: MultiAgent) => void;
    variants?: any;
}

export const itemVariants = {
    hidden: {
        opacity: 0,
        y: 10
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.3
        }
    }
};

const StatButton = ({
    icon: Icon,
    value,
    label,
    tooltip,
    active,
}: {
    icon: any;
    value: string | number;
    label: string;
    tooltip: string;
    active?: boolean;
}) => (
    <TooltipProvider delayDuration={200}>
        <Tooltip>
            <TooltipTrigger asChild>
                <div
                    className={cn(
                        "h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2 transition-colors",
                        active ? "bg-primary/5" : "hover:bg-muted/50"
                    )}
                >
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <Icon className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-semibold">{value}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">
                        {label}
                    </span>
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <p className="text-xs">{tooltip}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

export const MultiAgentCardSkeleton = () => (
    <Card className="shadow-sm">
        <div className="p-5 space-y-4">
            {/* Header with Icon, Title and ID */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Icon */}
                    <div className="h-10 w-10 rounded-lg bg-muted animate-pulse flex-shrink-0" />
                    
                    {/* Title and Date */}
                    <div className="flex-1 min-w-0">
                        <div className="h-5 w-[180px] bg-muted animate-pulse rounded mb-2" />
                        <div className="h-3.5 w-[100px] bg-muted/50 animate-pulse rounded" />
                    </div>
                </div>

                {/* ID on Right */}
                <div className="h-8 w-[140px] bg-muted animate-pulse rounded" />
            </div>

            {/* Grid */}
            <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                {/* Row 1 */}
                <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="h-11 flex items-center justify-center">
                        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    </div>
                </div>
            </div>
        </div>
    </Card>
);

export const MultiAgentSkeletonGrid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {[...Array(6)].map((_, index) => (
            <MultiAgentCardSkeleton key={index} />
        ))}
    </div>
);

const MultiAgentCard: React.FC<MultiAgentCardProps> = ({ agent, onClick, variants }) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Calculate total connections (destinations)
    const totalConnections = agent.members.reduce((total, member) => {
        return total + (member.assistantDestinations?.length || 0);
    }, 0);

    return (
        <motion.div variants={variants || itemVariants}>
            <Card
                className="group relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={() => onClick(agent)}
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm flex-shrink-0">
                                <Network className="h-5 w-5 text-primary" />
                            </div>
                            
                            {/* Title and Date */}
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold tracking-tight truncate">
                                    {agent.name}
                                </h3>
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                    <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="font-medium">{formatDate(agent.updatedAt)}</span>
                                </div>
                            </div>
                        </div>

                        {/* ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId
                                id={agent.id}
                                label="Multi Agent ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Grid - Single Row */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Assistants Count */}
                            <StatButton
                                icon={Users}
                                value={agent.members.length}
                                label="Assistants"
                                tooltip={`${agent.members.length} assistant${agent.members.length !== 1 ? 's' : ''} in workflow`}
                                active={agent.members.length > 0}
                            />

                            {/* Connections Count */}
                            <StatButton
                                icon={GitBranch}
                                value={totalConnections}
                                label="Connections"
                                tooltip={`${totalConnections} connection${totalConnections !== 1 ? 's' : ''} between assistants`}
                                active={totalConnections > 0}
                            />

                            {/* Created Date */}
                            <StatButton
                                icon={Calendar}
                                value={formatDate(agent.createdAt)}
                                label="Created"
                                tooltip={`Created on ${formatDate(agent.createdAt)}`}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};

export default MultiAgentCard;
